import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, EmptyState } from "../lib/components";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchConversation,
  fetchThreads,
  sendMessage,
  type ChatMessage,
  type ChatThread,
} from "../api/messages";
import {
  acceptProposal,
  cancelProposal,
  completeProposal,
  fetchActiveProposalWith,
  rejectProposal,
  type TradeProposal,
} from "../api/trades";
import { createReport } from "../api/reports";
import { createReview, fetchMyReviewForTrade } from "../api/reviews";
import { useToast } from "../lib/toast";
import { ApiError } from "../api/client";
import { useChatSocket } from "../lib/useChatSocket";

interface Props {
  initialUserId?: number | null;
}

const POLL_MS = 15000;  // fallback más espaciado porque WS hace lo principal

export function ChatScreen({ initialUserId }: Props) {
  const { user: me } = useAuth();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeUserId, setActiveUserId] = useState<number | null>(initialUserId ?? null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingConv, setLoadingConv] = useState(false);
  const [activeProposal, setActiveProposal] = useState<TradeProposal | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [applyInventory, setApplyInventory] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reviewTradeId, setReviewTradeId] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const activeUserIdRef = useRef<number | null>(activeUserId);
  activeUserIdRef.current = activeUserId;
  const toast = useToast();

  const reloadThreads = useCallback(() => {
    return fetchThreads()
      .then((t) => {
        setThreads(t);
        setActiveUserId((cur) => cur ?? (t.length > 0 ? t[0].peer.id : null));
      })
      .catch(() => null)
      .finally(() => setLoadingThreads(false));
  }, []);

  const reloadConversation = useCallback((uid: number, silent = false) => {
    if (!silent) setLoadingConv(true);
    return fetchConversation(uid)
      .then(setMessages)
      .catch(() => null)
      .finally(() => setLoadingConv(false));
  }, []);

  const reloadActiveProposal = useCallback((uid: number) => {
    return fetchActiveProposalWith(uid)
      .then(setActiveProposal)
      .catch(() => setActiveProposal(null));
  }, []);

  useEffect(() => {
    reloadThreads();
  }, [reloadThreads]);

  useEffect(() => {
    if (activeUserId) {
      reloadConversation(activeUserId);
      reloadActiveProposal(activeUserId);
    } else {
      setMessages([]);
      setActiveProposal(null);
    }
  }, [activeUserId, reloadConversation, reloadActiveProposal]);

  // WebSocket realtime: push de mensajes nuevos sin esperar al polling
  useChatSocket((event) => {
    if (event.type === "message") {
      const msg = event as unknown as ChatMessage;
      const peerId = activeUserIdRef.current;
      const isInThisChat =
        peerId !== null &&
        ((msg.from_user_id === peerId && msg.to_user_id === me?.id) ||
          (msg.from_user_id === me?.id && msg.to_user_id === peerId));
      if (isInThisChat) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
      // Refrescar lista de threads para actualizar last_message + unread
      reloadThreads();
    } else if (event.type === "trade_update") {
      const peerId = activeUserIdRef.current;
      if (peerId) reloadActiveProposal(peerId);
    }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeUserId) {
        reloadConversation(activeUserId, true);
        reloadActiveProposal(activeUserId);
      }
      reloadThreads();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [activeUserId, reloadConversation, reloadActiveProposal, reloadThreads]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages.length, activeUserId]);

  const send = async () => {
    if (!draft.trim() || !activeUserId || sending) return;
    setSending(true);
    const text = draft;
    setDraft("");
    try {
      const msg = await sendMessage(activeUserId, text);
      setMessages((m) => [...m, msg]);
      reloadThreads();
    } catch (e) {
      toast(`Error enviando: ${e}`);
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const handleAcceptPin = async () => {
    if (!activeProposal || !me) return;
    try {
      await acceptProposal(activeProposal.id);
      toast("¡Propuesta aceptada! Ya pueden coordinar la entrega aquí mismo.");
      if (activeUserId) {
        reloadActiveProposal(activeUserId);
        reloadConversation(activeUserId, true);
      }
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    }
  };

  const handleRejectPin = async () => {
    if (!activeProposal) return;
    if (!confirm("¿Rechazar esta propuesta?")) return;
    try {
      await rejectProposal(activeProposal.id);
      toast("Propuesta rechazada");
      if (activeUserId) {
        reloadActiveProposal(activeUserId);
        reloadConversation(activeUserId, true);
      }
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleCancelPin = async () => {
    if (!activeProposal) return;
    if (!confirm("¿Cancelar tu propuesta?")) return;
    try {
      await cancelProposal(activeProposal.id);
      toast("Propuesta cancelada");
      if (activeUserId) {
        reloadActiveProposal(activeUserId);
        reloadConversation(activeUserId, true);
      }
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const confirmComplete = async () => {
    if (!activeProposal) return;
    setCompleting(true);
    const completedId = activeProposal.id;
    try {
      await completeProposal(completedId, applyInventory);
      toast(applyInventory
        ? "¡Trato completado y álbum actualizado!"
        : "¡Trato completado! +1 trade en tu perfil");
      setCompleteOpen(false);
      if (activeUserId) {
        reloadActiveProposal(activeUserId);
        reloadConversation(activeUserId, true);
      }
      // Tras un instante, abrir modal de reseña si aún no la dejé
      setTimeout(async () => {
        try {
          const existing = await fetchMyReviewForTrade(completedId);
          if (!existing) setReviewTradeId(completedId);
        } catch { /* ignore */ }
      }, 800);
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setCompleting(false);
    }
  };

  const activePeer = threads.find((t) => t.peer.id === activeUserId)?.peer;

  if (!me) return null;

  if (!loadingThreads && threads.length === 0 && !activeUserId) {
    return (
      <div className="page" style={{ paddingTop: 8 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, margin: "0 0 16px", letterSpacing: "-0.02em" }}>Mensajes</h1>
        <EmptyState
          icon="chat"
          title="Sin conversaciones aún"
          body="Cuando contactes a otro coleccionista desde 'Matches' o 'Mercado', la conversación aparecerá aquí."
        />
      </div>
    );
  }

  return (
    <div className="page" style={{ paddingTop: 8 }}>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, margin: "0 0 16px", letterSpacing: "-0.02em" }}>Mensajes</h1>
      <div className="chat-wrap" data-active={activeUserId ? "true" : "false"}>
        <div className="chat-list">
          {threads.length === 0 && !loadingThreads && (
            <div style={{ padding: 16, color: "var(--fg-muted)", fontSize: 13, textAlign: "center" }}>
              {activeUserId ? "Esta es la primera conversación" : "Sin conversaciones"}
            </div>
          )}
          {threads.map((t) => (
            <div
              key={t.peer.id}
              className={"chat-list-item " + (t.peer.id === activeUserId ? "active" : "")}
              onClick={() => setActiveUserId(t.peer.id)}
            >
              <Avatar user={t.peer} />
              <div style={{ minWidth: 0 }}>
                <div className="who" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.peer.name}</div>
                <div className="preview" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.last_from_me ? "Tú: " : ""}{t.last_message_body || "(mensaje)"}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <div className="time">{relativeTime(t.last_message_at)}</div>
                {t.unread_count > 0 && (
                  <div style={{ background: "var(--accent-magenta)", color: "white", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 999, minWidth: 18, textAlign: "center" }}>
                    {t.unread_count}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {activeUserId ? (
          <div className="chat-main">
            <div className="chat-header">
              <button
                className="icon-btn chat-back-btn"
                onClick={() => setActiveUserId(null)}
                title="Volver"
                aria-label="Volver"
              >
                <Icon name="arrowL" size={16} />
              </button>
              <Avatar user={activePeer ?? null} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{activePeer?.name ?? `Usuario #${activeUserId}`}</div>
                <div className="tiny muted">
                  {activePeer ? <><Icon name="star" size={10} /> {Number(activePeer.stars_avg || 0).toFixed(1)} · {activePeer.city ?? "—"}</> : ""}
                </div>
              </div>
              <button
                className="icon-btn"
                onClick={() => setReportOpen(true)}
                title="Reportar usuario"
                aria-label="Reportar usuario"
                style={{ color: "var(--accent-magenta)" }}
              >
                🚩
              </button>
            </div>

            {activeProposal && (
              <ProposalPin
                proposal={activeProposal}
                myId={me.id}
                onAccept={handleAcceptPin}
                onReject={handleRejectPin}
                onCancel={handleCancelPin}
                onCompleteClick={() => { setApplyInventory(true); setCompleteOpen(true); }}
              />
            )}

            <div className="chat-body" ref={bodyRef}>
              {loadingConv && messages.length === 0 && (
                <div style={{ textAlign: "center", color: "var(--fg-muted)", padding: 24, fontSize: 13 }}>Cargando mensajes…</div>
              )}
              {messages.length === 0 && !loadingConv && (
                <div style={{ textAlign: "center", color: "var(--fg-muted)", padding: 24, fontSize: 13 }}>
                  Empieza la conversación con un saludo 👋
                </div>
              )}
              {messages.map((msg) => {
                if (msg.kind === "system") return <div key={msg.id} className="bubble system">{msg.body}</div>;
                const fromMe = msg.from_user_id === me.id;
                return (
                  <div key={msg.id} className={"bubble " + (fromMe ? "me" : "them")}>
                    {msg.body}
                    <div className="time">{relativeTime(msg.created_at)}</div>
                  </div>
                );
              })}
            </div>

            <div className="chat-input">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Escribe un mensaje…"
                disabled={sending}
              />
              <button className="btn btn-primary" onClick={send} disabled={sending || !draft.trim()}>
                <Icon name="send" size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="chat-main" style={{ display: "grid", placeItems: "center", color: "var(--fg-muted)" }}>
            <div style={{ textAlign: "center" }}>
              <Icon name="chat" size={32} />
              <div style={{ marginTop: 8 }}>Selecciona una conversación</div>
            </div>
          </div>
        )}
      </div>

      {reportOpen && activeUserId && (
        <ReportUserModal
          targetUserId={activeUserId}
          targetName={activePeer?.name ?? `Usuario #${activeUserId}`}
          onClose={() => setReportOpen(false)}
        />
      )}

      {completeOpen && activeProposal && (
        <CompleteConfirmModal
          giveCount={activeProposal.from_user.id === me.id ? activeProposal.give_count : activeProposal.receive_count}
          receiveCount={activeProposal.from_user.id === me.id ? activeProposal.receive_count : activeProposal.give_count}
          applyInventory={applyInventory}
          setApplyInventory={setApplyInventory}
          submitting={completing}
          onCancel={() => setCompleteOpen(false)}
          onConfirm={confirmComplete}
        />
      )}

      {reviewTradeId !== null && activePeer && (
        <ReviewModal
          tradeId={reviewTradeId}
          peerName={activePeer.name}
          onClose={() => setReviewTradeId(null)}
          onSent={() => setReviewTradeId(null)}
        />
      )}
    </div>
  );
}

function ReviewModal({ tradeId, peerName, onClose, onSent }: { tradeId: number; peerName: string; onClose: () => void; onSent: () => void }) {
  const [stars, setStars] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const submit = async () => {
    setSubmitting(true);
    try {
      await createReview({ trade_proposal_id: tradeId, stars, text: text.trim() || undefined });
      toast(`Reseña enviada · ⭐${stars}`);
      onSent();
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 460, width: "100%", padding: 22 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: "0 0 6px" }}>
          ⭐ Califica a {peerName}
        </h2>
        <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 18px" }}>
          ¿Cómo fue el intercambio? Las reseñas ayudan a otros coleccionistas a confiar.
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 20 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setStars(n)}
              style={{
                fontSize: 36,
                background: "transparent",
                border: 0,
                cursor: "pointer",
                color: n <= stars ? "var(--accent-amber)" : "var(--border)",
                lineHeight: 1,
                padding: 4,
                transition: "transform 0.1s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.15)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
              aria-label={`${n} estrellas`}
            >
              ★
            </button>
          ))}
        </div>

        <div className="field" style={{ marginBottom: 16 }}>
          <label>Comentario (opcional)</label>
          <textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ej: Excelente coleccionista, todo perfecto. ¡Volveré a cambiar!"
            maxLength={500}
          />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Después</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? "Enviando…" : "Enviar reseña"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProposalPinProps {
  proposal: TradeProposal;
  myId: number;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
  onCompleteClick: () => void;
}

function ProposalPin({ proposal: p, myId, onAccept, onReject, onCancel, onCompleteClick }: ProposalPinProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const isIncoming = p.to_user.id === myId;
  const giveItems = p.items.filter((it) => it.direction === "give");
  const receiveItems = p.items.filter((it) => it.direction === "receive");
  const myGive = isIncoming ? receiveItems : giveItems;
  const myReceive = isIncoming ? giveItems : receiveItems;
  const cashFromMyView = isIncoming ? -p.cash_delta : p.cash_delta;

  const isAccepted = p.status === "accepted";
  const isPending = p.status === "pending";

  const myGiveCount = myGive.reduce((sum, it) => sum + it.quantity, 0);
  const myReceiveCount = myReceive.reduce((sum, it) => sum + it.quantity, 0);

  const compactTitle = isAccepted
    ? "Trato aceptado"
    : isIncoming ? "Te proponen un trato" : "Tu propuesta pendiente";

  return (
    <>
      <div
        className={"chat-pin compact " + (isAccepted ? "accepted" : "pending")}
        onClick={() => setDetailOpen(true)}
        role="button"
        title="Click para ver el detalle del trato"
      >
        <div className="chat-pin-icon-compact">{isAccepted ? "🤝" : "📩"}</div>
        <div className="chat-pin-compact-info">
          <div className="chat-pin-compact-title">{compactTitle}</div>
          <div className="chat-pin-compact-summary">
            <b style={{ color: "var(--primary)" }}>{myGiveCount}</b>
            <span className="chat-pin-arrow">⇄</span>
            <b style={{ color: "var(--accent-amber)" }}>{myReceiveCount}</b>
            {cashFromMyView !== 0 && (
              <>
                <span style={{ color: "var(--fg-muted)" }}>·</span>
                <span className={cashFromMyView < 0 ? "chat-pin-cash in" : "chat-pin-cash out"}>
                  {cashFromMyView < 0 ? "+" : "−"}${Math.abs(cashFromMyView).toLocaleString("es-CO")}
                </span>
              </>
            )}
            <span className="chat-pin-detail-hint">· ver detalle</span>
          </div>
        </div>
        <div className="chat-pin-compact-actions" onClick={(e) => e.stopPropagation()}>
          {isPending && isIncoming && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={onReject}>Rechazar</button>
              <button className="btn btn-primary btn-sm" onClick={onAccept}>
                <Icon name="check" size={14} /> Aceptar
              </button>
            </>
          )}
          {isPending && !isIncoming && (
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
          )}
          {isAccepted && (
            <button className="btn btn-primary btn-sm" onClick={onCompleteClick}>
              <Icon name="check" size={14} /> Marcar completado
            </button>
          )}
        </div>
      </div>

      {detailOpen && (
        <ProposalDetailModal
          proposal={p}
          myGive={myGive}
          myReceive={myReceive}
          cashFromMyView={cashFromMyView}
          isAccepted={isAccepted}
          isIncoming={isIncoming}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </>
  );
}

interface DetailModalProps {
  proposal: TradeProposal;
  myGive: TradeProposal["items"];
  myReceive: TradeProposal["items"];
  cashFromMyView: number;
  isAccepted: boolean;
  isIncoming: boolean;
  onClose: () => void;
}

function ProposalDetailModal({ proposal: p, myGive, myReceive, cashFromMyView, isAccepted, isIncoming, onClose }: DetailModalProps) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 540, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 28, lineHeight: 1 }}>{isAccepted ? "🤝" : "📩"}</div>
          </div>
          <div style={{ flex: 1, marginLeft: 12 }}>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, margin: 0 }}>
              {isAccepted ? "Trato aceptado" : (isIncoming ? "Te proponen un trato" : "Tu propuesta")}
            </h2>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {isAccepted ? "Coordinen la entrega física por chat" : "Detalle del intercambio"}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        {p.message && (
          <div style={{ background: "var(--bg-subtle)", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 14 }}>
            💬 <i>"{p.message}"</i>
          </div>
        )}

        <div className="chat-pin-items" style={{ background: "var(--bg-subtle)", marginBottom: cashFromMyView !== 0 ? 12 : 0 }}>
          <div className="chat-pin-items-side">
            <div className="tiny bold muted">TÚ DAS ({myGive.reduce((s, it) => s + it.quantity, 0)})</div>
            <div className="chat-pin-chips">
              {myGive.map((it) => (
                <span key={it.id} className="chat-pin-chip primary" title={it.sticker_name}>
                  {it.sticker_num}{it.quantity > 1 ? `×${it.quantity}` : ""}
                </span>
              ))}
            </div>
            {myGive.map((it) => (
              <div key={"name-" + it.id} className="tiny muted" style={{ marginTop: 4 }}>{it.sticker_num} · {it.sticker_name}</div>
            ))}
          </div>
          <div className="chat-pin-items-side">
            <div className="tiny bold muted" style={{ textAlign: "right" }}>RECIBES ({myReceive.reduce((s, it) => s + it.quantity, 0)})</div>
            <div className="chat-pin-chips" style={{ justifyContent: "flex-end" }}>
              {myReceive.map((it) => (
                <span key={it.id} className="chat-pin-chip amber" title={it.sticker_name}>
                  {it.sticker_num}{it.quantity > 1 ? `×${it.quantity}` : ""}
                </span>
              ))}
            </div>
            {myReceive.map((it) => (
              <div key={"name-" + it.id} className="tiny muted" style={{ marginTop: 4, textAlign: "right" }}>{it.sticker_num} · {it.sticker_name}</div>
            ))}
          </div>
        </div>

        {cashFromMyView !== 0 && (
          <div style={{
            background: cashFromMyView < 0 ? "rgba(16,185,129,0.12)" : "rgba(236,72,153,0.12)",
            color: cashFromMyView < 0 ? "var(--primary)" : "var(--accent-magenta)",
            padding: "10px 14px",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 14,
          }}>
            {cashFromMyView < 0
              ? <>💵 La otra persona te encima <b>${Math.abs(cashFromMyView).toLocaleString("es-CO")}</b> COP</>
              : <>💸 Tú encimas <b>${Math.abs(cashFromMyView).toLocaleString("es-CO")}</b> COP</>}
          </div>
        )}

        <div className="tiny muted" style={{ textAlign: "center", marginTop: 8 }}>
          Propuesta #{p.id} · Estado: <b>{p.status}</b>
        </div>
      </div>
    </div>
  );
}

interface CompleteConfirmProps {
  giveCount: number;
  receiveCount: number;
  applyInventory: boolean;
  setApplyInventory: (v: boolean) => void;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ReportUserModal({ targetUserId, targetName, onClose }: { targetUserId: number; targetName: string; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const REASONS = [
    "Comportamiento abusivo o irrespetuoso",
    "Intento de estafa",
    "Spam / cuenta falsa",
    "No cumplió el intercambio acordado",
    "Otro",
  ];

  const submit = async () => {
    if (!reason) { toast("Selecciona un motivo"); return; }
    setSubmitting(true);
    try {
      await createReport({
        reported_user_id: targetUserId,
        reason,
        details: details.trim() || undefined,
      });
      toast("Reporte enviado · gracias por avisarnos");
      onClose();
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 460, width: "100%", padding: 22 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: "0 0 8px" }}>
          🚩 Reportar a {targetName}
        </h2>
        <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 16px" }}>
          Nuestro equipo revisa cada reporte. Para ayuda inmediata contacta <b>contacto@nilabs.co</b>.
        </p>
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Motivo</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="" disabled>Selecciona…</option>
            {REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Detalles (opcional)</label>
          <textarea
            rows={4}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Cuéntanos qué pasó (fechas, qué se acordó, etc.)"
            maxLength={2000}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || !reason}>
            {submitting ? "Enviando…" : "Enviar reporte"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompleteConfirmModal({ giveCount, receiveCount, applyInventory, setApplyInventory, submitting, onCancel, onConfirm }: CompleteConfirmProps) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 460, width: "100%", padding: 22 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>
          ¿Confirmar intercambio?
        </h2>
        <p style={{ color: "var(--fg-muted)", margin: "0 0 14px", fontSize: 14 }}>
          Confirma solo cuando ya hayan intercambiado las láminas físicas.
        </p>

        <div style={{ background: "var(--bg-subtle)", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-around" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", fontWeight: 700 }}>Tú entregas</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--primary)" }}>{giveCount}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", fontWeight: 700 }}>Tú recibes</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--accent-amber)" }}>{receiveCount}</div>
            </div>
          </div>
        </div>

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 12, border: "1px solid var(--border)", borderRadius: 10, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={applyInventory} onChange={(e) => setApplyInventory(e.target.checked)} style={{ marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Actualizar mi álbum digital automáticamente</div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 2 }}>
              Restará las que diste y sumará las que recibiste a tu inventario (y al del otro).
            </div>
          </div>
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={submitting}>
            {submitting ? "Confirmando…" : "✓ Confirmar trato"}
          </button>
        </div>
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
